
import { useState } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CircleDecor } from '@/components/ui/visual-elements';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { motion } from 'framer-motion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { fadeInVariants, slideInVariants } from '@/lib/animations';
import { Download, Users, HeartPulse, TrendingUp, Settings, CalendarDays, Activity, Brain } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const CorporateDashboard = () => {
  const [timeRange, setTimeRange] = useState('7days');
  const [department, setDepartment] = useState('all');
  
  // Dados de exemplo para o dashboard corporativo
  const teamWellbeingData = [
    { name: 'Seg', estresse: 65, ansiedade: 60, engagement: 71, satisfacao: 68 },
    { name: 'Ter', estresse: 68, ansiedade: 62, engagement: 69, satisfacao: 65 },
    { name: 'Qua', estresse: 75, ansiedade: 70, engagement: 65, satisfacao: 60 },
    { name: 'Qui', estresse: 70, ansiedade: 65, engagement: 67, satisfacao: 63 },
    { name: 'Sex', estresse: 60, ansiedade: 55, engagement: 72, satisfacao: 70 },
    { name: 'Sáb', estresse: 55, ansiedade: 50, engagement: 75, satisfacao: 73 },
    { name: 'Dom', estresse: 52, ansiedade: 48, engagement: 78, satisfacao: 75 },
  ];
  
  const engagementData = [
    { name: 'Meditações', value: 35 },
    { name: 'Registro Emocional', value: 25 },
    { name: 'Assistente IA', value: 20 },
    { name: 'Conteúdos', value: 15 },
    { name: 'Testes', value: 5 },
  ];
  
  const departmentData = [
    { name: 'Marketing', estresseMedia: 68, ansiedadeMedia: 65, engagementRate: 75, participationRate: 82 },
    { name: 'Engenharia', estresseMedia: 72, ansiedadeMedia: 68, engagementRate: 70, participationRate: 78 },
    { name: 'Vendas', estresseMedia: 74, ansiedadeMedia: 70, engagementRate: 65, participationRate: 75 },
    { name: 'Financeiro', estresseMedia: 65, ansiedadeMedia: 60, engagementRate: 68, participationRate: 80 },
    { name: 'RH', estresseMedia: 58, ansiedadeMedia: 55, engagementRate: 85, participationRate: 90 },
  ];
  
  const COLORS = ['#13265a', '#60a5fa', '#34d399', '#f97316', '#8b5cf6'];

  return (
    <MainLayout>
      <div className="container mx-auto p-6 relative">
        <CircleDecor className="absolute top-10 right-10 opacity-10" variant="secondary" size="lg" />
        <CircleDecor className="absolute bottom-20 left-10 opacity-10" size="sm" />
        
        <motion.div 
          className="flex justify-between items-center mb-8"
          variants={fadeInVariants}
          initial="initial"
          animate="animate"
        >
          <div>
            <h1 className="text-2xl font-bold">dashboard corporativo</h1>
            <p className="text-gray-600">acompanhamento do bem-estar da equipe</p>
          </div>
          
          <div className="flex gap-2">
            <Select
              value={department}
              onValueChange={setDepartment}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Departamento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="marketing">Marketing</SelectItem>
                <SelectItem value="engineering">Engenharia</SelectItem>
                <SelectItem value="sales">Vendas</SelectItem>
                <SelectItem value="finance">Financeiro</SelectItem>
                <SelectItem value="hr">RH</SelectItem>
              </SelectContent>
            </Select>
            
            <Select
              value={timeRange}
              onValueChange={setTimeRange}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Período" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7days">Últimos 7 dias</SelectItem>
                <SelectItem value="30days">Últimos 30 dias</SelectItem>
                <SelectItem value="90days">Últimos 90 dias</SelectItem>
                <SelectItem value="year">Este ano</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" className="flex items-center gap-1">
              <Download size={16} /> Relatório
            </Button>
          </div>
        </motion.div>
        
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
          variants={slideInVariants.up}
          initial="initial"
          animate="animate"
          transition={{ staggerChildren: 0.1 }}
        >
          <motion.div variants={fadeInVariants}>
            <Card className="overflow-hidden">
              <CardHeader className="pb-2 bg-gradient-to-r from-blue-50 to-white">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-brand-blue" />
                  <CardTitle className="text-xl">Taxa de Participação</CardTitle>
                </div>
                <CardDescription>Total da empresa</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="text-3xl font-bold text-brand-blue">78%</div>
                <p className="text-sm text-gray-600 flex items-center">
                  <span className="inline-block w-4 h-4 mr-1 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">+</span>
                  3.2% do mês anterior
                </p>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div variants={fadeInVariants}>
            <Card className="overflow-hidden">
              <CardHeader className="pb-2 bg-gradient-to-r from-red-50 to-white">
                <div className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-red-500" />
                  <CardTitle className="text-xl">Nível de Estresse</CardTitle>
                </div>
                <CardDescription>Média da empresa</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="text-3xl font-bold text-red-500">63.5%</div>
                <p className="text-sm text-gray-600 flex items-center">
                  <span className="inline-block w-4 h-4 mr-1 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">-</span>
                  2.1% do mês anterior
                </p>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div variants={fadeInVariants}>
            <Card className="overflow-hidden">
              <CardHeader className="pb-2 bg-gradient-to-r from-green-50 to-white">
                <div className="flex items-center gap-2">
                  <HeartPulse className="h-5 w-5 text-green-500" />
                  <CardTitle className="text-xl">Bem-estar</CardTitle>
                </div>
                <CardDescription>Índice geral</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="text-3xl font-bold text-green-500">72.8%</div>
                <p className="text-sm text-gray-600 flex items-center">
                  <span className="inline-block w-4 h-4 mr-1 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">+</span>
                  4.5% do mês anterior
                </p>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div variants={fadeInVariants}>
            <Card className="overflow-hidden">
              <CardHeader className="pb-2 bg-gradient-to-r from-purple-50 to-white">
                <div className="flex items-center gap-2">
                  <Brain className="h-5 w-5 text-purple-500" />
                  <CardTitle className="text-xl">Engajamento</CardTitle>
                </div>
                <CardDescription>Atividade na plataforma</CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="text-3xl font-bold text-purple-500">85.0%</div>
                <p className="text-sm text-gray-600 flex items-center">
                  <span className="inline-block w-4 h-4 mr-1 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">+</span>
                  7.8% do mês anterior
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
        
        <motion.div 
          className="mb-8"
          variants={fadeInVariants}
          initial="initial"
          animate="animate"
          transition={{ delay: 0.2 }}
        >
          <Tabs defaultValue="wellbeing">
            <div className="flex items-center justify-between mb-4">
              <TabsList>
                <TabsTrigger value="wellbeing">Bem-estar</TabsTrigger>
                <TabsTrigger value="engagement">Engajamento</TabsTrigger>
                <TabsTrigger value="department">Departamentos</TabsTrigger>
              </TabsList>
              <Button variant="ghost" size="sm" className="flex items-center gap-1">
                <CalendarDays size={16} /> Personalizar Período
              </Button>
            </div>
            
            <TabsContent value="wellbeing">
              <Card>
                <CardHeader>
                  <CardTitle>Tendências de Bem-estar</CardTitle>
                  <CardDescription>Métricas de estresse, ansiedade e engajamento da equipe</CardDescription>
                </CardHeader>
                <CardContent className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={teamWellbeingData}
                      margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="estresse" 
                        stroke="#f87171" 
                        name="Estresse"
                        strokeWidth={2}
                        activeDot={{ r: 8 }} 
                      />
                      <Line 
                        type="monotone" 
                        dataKey="ansiedade" 
                        stroke="#fb923c" 
                        name="Ansiedade"
                        strokeWidth={2}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="engagement" 
                        stroke="#60a5fa" 
                        name="Engajamento"
                        strokeWidth={2}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="satisfacao" 
                        stroke="#34d399" 
                        name="Satisfação"
                        strokeWidth={2}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="engagement">
              <Card>
                <CardHeader>
                  <CardTitle>Distribuição de Engajamento</CardTitle>
                  <CardDescription>Uso de recursos da plataforma pela equipe</CardDescription>
                </CardHeader>
                <CardContent className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={engagementData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                        label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {engagementData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="department">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Comparação por Departamento</CardTitle>
                    <CardDescription>Métricas de bem-estar por departamento</CardDescription>
                  </div>
                  <Button variant="ghost" size="icon">
                    <Settings className="h-4 w-4" />
                  </Button>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Departamento</TableHead>
                        <TableHead>Estresse</TableHead>
                        <TableHead>Ansiedade</TableHead>
                        <TableHead>Engajamento</TableHead>
                        <TableHead>Participação</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {departmentData.map((dept, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">{dept.name}</TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              <div className="h-2 w-full max-w-[60px] bg-gray-200 rounded-full mr-2 overflow-hidden">
                                <div className="h-full bg-red-400 rounded-full" style={{width: `${dept.estresseMedia}%`}}></div>
                              </div>
                              {dept.estresseMedia}%
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              <div className="h-2 w-full max-w-[60px] bg-gray-200 rounded-full mr-2 overflow-hidden">
                                <div className="h-full bg-orange-400 rounded-full" style={{width: `${dept.ansiedadeMedia}%`}}></div>
                              </div>
                              {dept.ansiedadeMedia}%
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              <div className="h-2 w-full max-w-[60px] bg-gray-200 rounded-full mr-2 overflow-hidden">
                                <div className="h-full bg-blue-400 rounded-full" style={{width: `${dept.engagementRate}%`}}></div>
                              </div>
                              {dept.engagementRate}%
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              <div className="h-2 w-full max-w-[60px] bg-gray-200 rounded-full mr-2 overflow-hidden">
                                <div className="h-full bg-green-400 rounded-full" style={{width: `${dept.participationRate}%`}}></div>
                              </div>
                              {dept.participationRate}%
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </motion.div>
        
        <motion.div 
          className="mt-8 p-6 bg-brand-blue/5 rounded-xl border border-brand-blue/20"
          variants={fadeInVariants}
          initial="initial"
          animate="animate"
          transition={{ delay: 0.4 }}
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="bg-brand-blue/10 p-3 rounded-full">
                <TrendingUp className="text-brand-blue w-6 h-6" />
              </div>
              <div>
                <h3 className="font-medium text-lg">Relatório de ROI</h3>
                <p className="text-sm text-gray-600">Impacto das iniciativas de bem-estar na produtividade</p>
              </div>
            </div>
            <Button className="bg-brand-blue text-white hover:bg-opacity-90">
              Gerar Relatório Completo
            </Button>
          </div>
        </motion.div>
      </div>
    </MainLayout>
  );
};

export default CorporateDashboard;
